import { useCallback, useEffect, useState } from 'react'
import { erreurDeRetourOAuth } from '../api/google'
import { connecterParEmail, creerCompte, deconnecter, verifierAcces } from '../api/session'
import { sourceSupabase } from '../api/donnees'
import { supabase } from '../api/supabase'
import type { Source } from '../api/types'
import { Connexion } from './Connexion'
import { Console } from './Console'

type Etat =
  | { statut: 'demarrage' }
  | { statut: 'connexion' }
  | { statut: 'console'; email: string; source: Source }

/**
 * Aperçu de développement : `?apercu` ouvre la console avec des données
 * FICTIVES, sans connexion. Le test est sur `import.meta.env.DEV`, constante
 * remplacée à la compilation : en production ce bloc et le module d'aperçu
 * disparaissent du bundle — aucune porte dérobée.
 */
const APERCU = import.meta.env.DEV && new URLSearchParams(window.location.search).has('apercu')

export function App() {
  const [etat, setEtat] = useState<Etat>({ statut: 'demarrage' })
  const [erreur, setErreur] = useState<string | null>(null)

  /** Contrôle d'accès commun aux trois voies : e-mail, Google, restauration de session. */
  const entrer = useCallback(async (): Promise<void> => {
    const r = await verifierAcces()
    if (!r.ok) {
      setErreur(r.message)
      setEtat({ statut: 'connexion' })
      return
    }
    setErreur(null)
    setEtat({ statut: 'console', email: r.email, source: sourceSupabase(r.id) })
  }, [])

  useEffect(() => {
    let annule = false
    void (async () => {
      if (APERCU) {
        const { sourceApercu } = await import('../api/apercu')
        if (!annule) setEtat({ statut: 'console', email: 'apercu@exemple.test', source: sourceApercu() })
        return
      }
      // L'erreur OAuth est lue AVANT d'attendre supabase-js, qui consomme l'URL.
      const erreurOAuth = erreurDeRetourOAuth()
      try {
        const { data } = await supabase.auth.getSession() // attend l'échange du code Google
        if (annule) return
        if (erreurOAuth) setErreur(erreurOAuth)
        if (data.session) await entrer()
        else setEtat({ statut: 'connexion' })
      } catch {
        if (!annule) setEtat({ statut: 'connexion' })
      }
    })()
    return () => { annule = true }
  }, [entrer])

  /* Connexion par numéro + code WhatsApp : le code est vérifié par le composant, puis le MÊME contrôle d'accès
     `is_platform_admin()` que pour un mot de passe ou Google. */
  const avecWhatsapp = useCallback(async (_userId: string) => {
    setErreur(null)
    await entrer()
  }, [entrer])

  const seConnecter = useCallback(async (email: string, motDePasse: string) => {
    setErreur(null)
    try {
      await connecterParEmail(email, motDePasse)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Connexion refusée.')
      return
    }
    await entrer()
  }, [entrer])

  /* Création de compte : n'accorde AUCUN droit. Si une session s'ouvre (confirmation
     e-mail désactivée), `is_platform_admin()` refuse le compte comme à l'ordinaire. */
  const sInscrire = useCallback(async (nom: string, email: string, motDePasse: string) => {
    setErreur(null)
    const r = await creerCompte(nom, email, motDePasse)
    if (r.sessionOuverte) await entrer()
    return r
  }, [entrer])

  const seDeconnecter = useCallback(async () => {
    if (!APERCU) await deconnecter()
    setErreur(null)
    setEtat({ statut: 'connexion' })
  }, [])

  if (etat.statut === 'demarrage') return <div className="demarrage" aria-busy="true">Chargement…</div>
  if (etat.statut === 'connexion') return (
      <Connexion
        surConnexion={seConnecter}
        surConnexionWhatsapp={avecWhatsapp}
        surInscription={sInscrire}
        surChangementMode={() => setErreur(null)}
        erreur={erreur}
      />
    )
  return <Console source={etat.source} email={etat.email} surDeconnexion={() => void seDeconnecter()} />
}
